import React, { useEffect, useState, useCallback, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';

// Utils
import { getUserFormattedDate } from 'Core/data/Helpers';
import { NotificationManager } from 'Components/Notifications';

import Card from "@mui/material/Card";
import CardContent from '@mui/material/CardContent';
import { Grid } from "@mui/material";

import {
	Button,
	Modal,
	Checkbox
} from '@gpstrackit/gtc-react-controls';
import { NotificationContainer } from "Components/Notifications";
import Tabs, { TabContainer } from 'Components/Tabs';

//Actions
import {
	changeVehicles,
	clearRedux,
} from 'Redux/actions';
import {
	dtcGetDeviceDTC,
	openDTCModal,
	dtcClearDTC,
	reset,
} from './redux/slice';

//Styles
import './styles.scss';

const CustomCard = (props) => {
	const { children, styles, checkboxValue, id, hideCheckbox } = props

	return (
		<Card style={{ marginTop: 5, ...styles }}>
			<CardContent style={{ padding: '10px 5px' }}>
				<Grid
					container
					alignItems="center"
					justifyContent="space-between"
				>
					{
						!hideCheckbox && (
							<Grid item xs={1}>
								<Checkbox
									id={id}
									checked={checkboxValue}
									value={checkboxValue}
									onChange={props.onChange}
								/>
							</Grid>
						)
					}
					<Grid item xs={11}>
						{
							children
						}
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
}

const DiagnosticCodes = (props) => {
	const dispatch = useDispatch();

	const {
		open,
		loading,
		device: vehicle,
		codes,
		loadingClearDTC,
		clearDTCResponse,
		numberTab,
		history
	} = useSelector(state => state.dtcRedux);
	const { user } = useSelector(state => state.securityRedux);
	const { getMarkerDetailsResponse } = useSelector(state => state.infoBubbleRedux);
	const { vehicles } = useSelector(state => state.maintenanceVehiclesTabRedux)

	const { messages } = useIntl();

	const [tab, setTab] = useState(history ? 1 : 0);
	const [selectedItems, setSelectedItems] = useState([]);

	useEffect(() => {
		if (open && clearDTCResponse && clearDTCResponse.Message) {
			dispatch(dtcGetDeviceDTC({ device: vehicle, history: true }));
			const total = selectedItems?.length;
			setSelectedItems([]);
			NotificationManager.success(
				'Success',
				clearDTCResponse.Message,
				3000,
				null,
				null,
				'warning',
			);
			dispatch(reset({ clearDTCResponse: null, codes: [] }))
			setTab(1);
			if (getMarkerDetailsResponse) {
				dispatch(clearRedux('INFO_BUBBLE', {
					getMarkerDetailsResponse: {
						...getMarkerDetailsResponse,
						markerDetail: {
							...getMarkerDetailsResponse?.markerDetail,
							unit: {
								...getMarkerDetailsResponse?.markerDetail?.unit,
								dtcCount: {
									history: getMarkerDetailsResponse?.markerDetail?.unit?.dtcCount?.history + total,
									active: getMarkerDetailsResponse?.markerDetail?.unit?.dtcCount?.active - total,
								}
							}
						}
					}
				}));
			}
			if (vehicles) {
				let newVehicles = vehicles?.map(v => {
					if (v?.id == vehicle?.id) {
						return {
							...vehicle,
							dtcCount: {
								history: vehicle?.dtcCount?.history + total,
								active: vehicle?.dtcCount?.active - total,
							}
						}
					}
					return v;
				});
				dispatch(changeVehicles({ vehicles: newVehicles }));
			}
		}
	}, [clearDTCResponse, open]);

	useEffect(() => {
		if (numberTab === 1 && open) setSelectedItems([]);
	}, [numberTab, open]);

	useEffect(() => {
		setTab(history ? 1 : 0);
	}, [history]);

	const handleClose = useCallback(() => {
		dispatch(openDTCModal(false));
		dispatch(reset())
		setSelectedItems([])
	});

	const handleTabChange = useCallback(index => {
		if (index !== tab) {
			dispatch(reset({ codes: [] }));
			dispatch(dtcGetDeviceDTC({ device: vehicle, history: index !== 0 }));
			setTab(index);
			setSelectedItems([]);
		}
	}, [dispatch, tab, vehicle]);

	const getDescription = useCallback((code) => {
		if (!code?.description || typeof code?.description != 'string') return (<span>---</span>);

		let description = code?.description?.replace("[{description=", '');
		description = description?.replace("}]", '');
		return (<span>{description} ({code?.occurrences || 1})</span>);
	});

	const getDate = useCallback((code) => {
		if (!code?.lastUnitTime) return (<span>---</span>);
		let date = getUserFormattedDate(user, parseInt(code?.lastUnitTime), messages, 'h:mm:ss A');
		return (<span>{date}</span>);
	});

	const handleClear = () => {
		const ids = selectedItems.map(item => item.id);
		dispatch(dtcClearDTC({ deviceId: vehicle.id, ids }));
	};

	// --- Renderers ---
	const renderCardContent = (item, withCheckbox = true) => (
		<CustomCard
			id={item.id}
			key={item.id}
			checkboxValue={selectedItems.some(i => i.id === item.id)}
			onChange={value => {
				setSelectedItems(prev => (
					value
						? [...prev, item].filter((v, i, arr) => arr.findIndex(o => o.id === v.id) === i)
						: prev.filter(i => i.id !== item.id)
				));
			}}
			hideCheckbox={!withCheckbox}
		>
			<div style={{ display: 'grid' }}>
				<div><strong>{messages['diagnosticCodes_code']}</strong> {item.dtcCode}</div>
				<div><strong>{messages['diagnosticCodes_definition']}</strong> {getDescription(item)}</div>
				<div><strong>{messages['diagnosticCodes_when']}</strong> {getDate(item)}</div>
			</div>
		</CustomCard>
	);

	const renderTabContent = (withCheckbox) => (
		codes.length
			? codes.map(item => renderCardContent(item, withCheckbox))
			: <div style={{ display: 'flex', justifyContent: 'center', opacity: 0.6 }}>{messages['diagnosticCodes_noData']}</div>
	);

	const title = `${messages['diagnosticCodes_title']} '${vehicle?.Label || vehicle?.label || ''}'`;

	// --- JSX ---
	return (
		<Modal
			title={title}
			open={open}
			position="right"
			size="small"
			handleClose={handleClose}
			loading={loading || loadingClearDTC}
			footer={(
				<div className="dtc-footer">
					<Button
						isDisabled={!selectedItems.length || loadingClearDTC}
						variant="secondary"
						style={{
							marginRight: 8,
							backgroundColor: 'black',
							color: 'white',
							opacity: !selectedItems.length ? 0.5 : 1
						}}
						onClick={handleClear}
					>
						{messages['diagnosticCodes_clear']}
					</Button>
					<Button isDisabled={loading} onClick={handleClose} variant="primary">
						{messages['diagnosticCodes_done']}
					</Button>
				</div>
			)}
		>
			<div className="dtc-container">
				<Tabs tab={tab} onChange={handleTabChange}>
					<TabContainer label={messages['diagnosticCodes_active']} index={0}>
						{renderTabContent(true)}
					</TabContainer>
					<TabContainer label={messages['diagnosticCodes_history']} index={1}>
						{renderTabContent(false)}
					</TabContainer>
				</Tabs>
			</div>
			<NotificationContainer />
		</Modal>
	);
};

export default memo(DiagnosticCodes);